import { useEffect, useState } from 'react';
import { ErrorComponent, type ErrorComponentProps } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import {
  claimRouteChunkReload,
  hasRecentRouteChunkReload,
  isChunkLoadError,
  isRouteChunkReloadUnderWay,
  routeChunkReloadStallRemainingMs,
} from '@/lib/chunk-load-recovery';
import { RoutePendingFallback } from './route-pending-fallback';

/**
 * The router's error UI. A page whose code file is gone (a deploy since this tab opened) reloads onto the same URL —
 * the address already shows the page being opened — keeping the loading look up until the new page arrives. It
 * offers Refresh instead when the device is offline (a reload would land on the browser's offline page) or the page
 * reloaded for this a moment ago (no loop). Any other error keeps TanStack's standard error view.
 */
export function RouteErrorFallback({ error }: ErrorComponentProps) {
  const chunkMissing = isChunkLoadError(error);
  // Decided once per error screen: re-renders while the reload is under way must not flip it to the notice.
  const [offline] = useState(() => navigator.onLine === false);
  const [reloading, setReloading] = useState(
    () => chunkMissing && (isRouteChunkReloadUnderWay() || (!offline && !hasRecentRouteChunkReload()))
  );
  useEffect(() => {
    if (!reloading) return;
    // Already reloading (React's development double effect run, a screen mounted while unloading): don't claim again.
    // Otherwise the reload is recorded only now, so a render React throws away can't use up the minute.
    if (!isRouteChunkReloadUnderWay()) {
      if (!claimRouteChunkReload()) {
        setReloading(false);
        return;
      }
      window.location.reload();
    }
    // A reload that never happens (cancelled by a leave-page prompt) must not leave the loading look up for good.
    const stall = window.setTimeout(() => setReloading(false), routeChunkReloadStallRemainingMs());
    return () => window.clearTimeout(stall);
  }, [reloading]);

  if (!chunkMissing) return <ErrorComponent error={error} />;
  if (reloading) return <RoutePendingFallback />;
  return (
    <div role="alert" className="flex flex-col items-start gap-3 p-4 md:p-6">
      <p className="text-[16px] text-clovie-text">
        {offline
          ? 'This page couldn’t load. Check the internet connection, then refresh.'
          : 'This page couldn’t load. Hayven may have just been updated.'}
      </p>
      <Button type="button" className="text-[16px]" onClick={() => window.location.reload()}>
        Refresh
      </Button>
    </div>
  );
}
